import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Building2, 
  Users, 
  Layers, 
  Shield, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  Activity,
  Server,
  Database,
  Zap,
  TrendingUp,
  Globe,
  Lock,
  Eye,
  RefreshCw,
  ChevronRight,
  BarChart3,
  FileText,
  Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MediaKshetra Brand Colors
const GOLD = '#D4A84B';
const GOLD_MUTED = '#B8975F';

export const SuperAdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [governance, setGovernance] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        credentials: 'include'
      });
      
      if (response.status === 403) {
        setIsAuthorized(false);
        toast.error('Super Admin access required');
        navigate('/dashboard');
        return;
      }
      
      if (response.ok) {
        setIsAuthorized(true);
        await fetchAllData();
      }
    } catch (error) {
      console.error('Authorization check failed:', error);
      navigate('/dashboard');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, govRes, orgsRes, usersRes, wsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/governance`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/organizations`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/users?limit=50`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/workspaces?limit=50`, { credentials: 'include' })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (govRes.ok) setGovernance(await govRes.json());
      if (orgsRes.ok) setOrganizations(await orgsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (wsRes.ok) setWorkspaces(await wsRes.json());
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = GOLD }) => (
    <Card className="hover:border-primary/30 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{title}</p>
            <p className="text-3xl font-light">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-3">
            <TrendingUp className="w-3 h-3 text-green-500" />
            <span className="text-xs text-green-500">{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const GovernanceCard = ({ title, value, status, icon: Icon }) => {
    const statusColors = {
      healthy: 'text-green-500',
      warning: 'text-amber-500',
      critical: 'text-red-500'
    };
    
    return (
      <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${statusColors[status] || 'text-foreground'}`}>
            {value}
          </span>
          {status === 'healthy' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
          {status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
          {status === 'critical' && <AlertTriangle className="w-4 h-4 text-red-500" />}
        </div>
      </div>
    );
  };

  if (!isAuthorized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0E0E0E' }}>
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: GOLD }} />
          <p className="text-muted-foreground">Verifying Super Admin access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0E0E0E' }}>
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-light tracking-tight text-white">Super Admin Console</h1>
              <Badge variant="outline" className="text-[10px]" style={{ borderColor: GOLD, color: GOLD }}>
                <Lock className="w-2.5 h-2.5 mr-1" />
                RESTRICTED
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              MediaKshetra GIOS™ — System-wide oversight and governance
            </p>
          </div>
          <Button 
            onClick={fetchAllData}
            variant="outline"
            size="sm"
            className="text-xs"
            style={{ borderColor: `${GOLD}40` }}
          >
            <RefreshCw className="w-3 h-3 mr-2" />
            Refresh Data
          </Button>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-secondary/30 p-1">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="organizations" className="text-xs">Organizations</TabsTrigger>
            <TabsTrigger value="users" className="text-xs">Users</TabsTrigger>
            <TabsTrigger value="workspaces" className="text-xs">Workspaces</TabsTrigger>
            <TabsTrigger value="governance" className="text-xs">Governance</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                icon={Building2} 
                title="Organizations" 
                value={stats?.organizations?.total || 0}
                subtitle={`${stats?.organizations?.agencies || 0} agencies, ${stats?.organizations?.clients || 0} clients`}
              />
              <StatCard 
                icon={Users} 
                title="Total Users" 
                value={stats?.users?.total || 0}
                subtitle={`${stats?.users?.active || 0} active`}
                trend="+12% this month"
              />
              <StatCard 
                icon={Layers} 
                title="Workspaces" 
                value={stats?.workspaces?.total || 0}
                subtitle={`${stats?.workspaces?.active || 0} active`}
              />
              <StatCard 
                icon={Zap} 
                title="AI Requests" 
                value={stats?.ai_usage?.total_requests || 0}
                subtitle="Total processed"
              />
            </div>

            {/* Subscription Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">Subscription Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {['free', 'basic', 'pro', 'enterprise'].map((tier) => (
                    <div key={tier} className="text-center p-4 rounded-lg bg-secondary/30">
                      <p className="text-2xl font-light">{stats?.users?.by_tier?.[tier] || 0}</p>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{tier}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* System Health & Governance Overview */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4" style={{ color: GOLD }} />
                    Governance Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <GovernanceCard 
                    title="Security Events (30d)" 
                    value={governance?.security?.events_last_30_days || 0}
                    status={governance?.security?.events_last_30_days > 10 ? 'warning' : 'healthy'}
                    icon={Shield}
                  />
                  <GovernanceCard 
                    title="Pending Approvals" 
                    value={governance?.compliance?.pending_approvals || 0}
                    status={governance?.compliance?.pending_approvals > 5 ? 'warning' : 'healthy'}
                    icon={Clock}
                  />
                  <GovernanceCard 
                    title="Platform Alerts" 
                    value={governance?.platform_health?.active_alerts || 0}
                    status={governance?.platform_health?.active_alerts > 0 ? 'warning' : 'healthy'}
                    icon={AlertTriangle}
                  />
                  <GovernanceCard 
                    title="System Health" 
                    value={governance?.platform_health?.platforms_healthy ? 'Operational' : 'Degraded'}
                    status={governance?.platform_health?.platforms_healthy ? 'healthy' : 'critical'}
                    icon={Activity}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Server className="w-4 h-4" style={{ color: GOLD }} />
                    Infrastructure Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <GovernanceCard 
                    title="Connected Platforms" 
                    value={stats?.platforms?.connected || 0}
                    status="healthy"
                    icon={Globe}
                  />
                  <GovernanceCard 
                    title="Total Platforms" 
                    value={stats?.platforms?.total || 0}
                    status="healthy"
                    icon={Database}
                  />
                  <GovernanceCard 
                    title="API Gateway" 
                    value="Operational"
                    status="healthy"
                    icon={Activity}
                  />
                  <GovernanceCard 
                    title="Database Cluster" 
                    value="Healthy"
                    status="healthy"
                    icon={Database}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium">All Organizations</CardTitle>
                <Badge variant="outline" className="text-xs">{organizations.length} total</Badge>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {organizations.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No organizations yet</p>
                    ) : (
                      organizations.map((org, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${GOLD}15` }}>
                              <Building2 className="w-5 h-5" style={{ color: GOLD }} />
                            </div>
                            <div>
                              <p className="font-medium">{org.name}</p>
                              <p className="text-xs text-muted-foreground">{org.type} • {org.status}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="text-[10px]">{org.type}</Badge>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium">All Users</CardTitle>
                <Badge variant="outline" className="text-xs">{users.length} loaded</Badge>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {users.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No users yet</p>
                    ) : (
                      users.map((user, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/20">
                              <span className="text-sm font-medium">{(user.name || user.email || 'U')[0].toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-medium">{user.name || 'Unknown User'}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="text-[10px]">{user.subscription_tier || 'free'}</Badge>
                            {user.system_role && (
                              <Badge className="text-[10px]" style={{ backgroundColor: GOLD, color: '#000' }}>
                                {user.system_role}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workspaces Tab */}
          <TabsContent value="workspaces">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium">All Workspaces</CardTitle>
                <Badge variant="outline" className="text-xs">{workspaces.length} loaded</Badge>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {workspaces.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No workspaces yet</p>
                    ) : (
                      workspaces.map((ws, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${GOLD}15` }}>
                              <Layers className="w-5 h-5" style={{ color: GOLD }} />
                            </div>
                            <div>
                              <p className="font-medium">{ws.name}</p>
                              <p className="text-xs text-muted-foreground">{ws.description || 'No description'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="text-[10px]">{ws.status || 'active'}</Badge>
                            <span className="text-xs text-muted-foreground">{ws.members?.length || 0} members</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Governance Tab */}
          <TabsContent value="governance">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Compliance Heatmap */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4" style={{ color: GOLD }} />
                    Compliance Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-500">Low Risk</span>
                      <span className="text-sm">85%</span>
                    </div>
                    <Progress value={85} className="h-2" />
                  </div>
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-amber-500">Moderate Risk</span>
                      <span className="text-sm">12%</span>
                    </div>
                    <Progress value={12} className="h-2" />
                  </div>
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-red-500">High Risk</span>
                      <span className="text-sm">3%</span>
                    </div>
                    <Progress value={3} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Audit Log */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" style={{ color: GOLD }} />
                    Recent Audit Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {[
                        { event: 'User login', user: 'admin@mediakshetra.com', time: '2 min ago', type: 'info' },
                        { event: 'Report generated', user: 'analyst@client.com', time: '15 min ago', type: 'info' },
                        { event: 'API rate limit warning', user: 'system', time: '1 hour ago', type: 'warning' },
                        { event: 'New client onboarded', user: 'admin@mediakshetra.com', time: '3 hours ago', type: 'success' },
                        { event: 'Password reset', user: 'user@example.com', time: '1 day ago', type: 'info' },
                      ].map((log, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                          <div className={`w-2 h-2 rounded-full ${
                            log.type === 'warning' ? 'bg-amber-500' :
                            log.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                          }`} />
                          <div className="flex-1">
                            <p className="text-sm">{log.event}</p>
                            <p className="text-xs text-muted-foreground">{log.user}</p>
                          </div>
                          <span className="text-xs text-muted-foreground">{log.time}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* API Health Monitor */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Activity className="w-4 h-4" style={{ color: GOLD }} />
                  API Health Monitor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { name: 'Google Trends API', status: 'operational', latency: '124ms' },
                    { name: 'Auth Service', status: 'operational', latency: '45ms' },
                    { name: 'MongoDB Cluster', status: 'operational', latency: '12ms' },
                    { name: 'AI Gateway', status: 'operational', latency: '890ms' },
                  ].map((api, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2 h-2 rounded-full ${
                          api.status === 'operational' ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        <span className="text-xs font-medium">{api.name}</span>
                      </div>
                      <p className="text-lg font-light">{api.latency}</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{api.status}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">
            MediaKshetra GIOS™ Super Admin Console — Restricted Access
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPage;
